import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Circle, SkipForward, RotateCcw, Shield, Plus, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_API || "";

export function OnboardingAssistant() {
  const [data, setData] = useState(null);
  const headers = useCallback(() => { const t = localStorage.getItem("token") || sessionStorage.getItem("token"); return { Authorization: `Bearer ${t}` }; }, []);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/onboarding/status`, { headers: headers() }); setData(r.data); } catch (e) {}
  }, [headers]);

  useEffect(() => { fetch(); }, [fetch]);

  const completeStep = async (stepId) => {
    await axios.post(`${API}/api/onboarding/complete-step`, { step_id: stepId }, { headers: headers() });
    toast.success("Schritt abgeschlossen"); fetch();
  };
  const skipStep = async (stepId) => {
    await axios.post(`${API}/api/onboarding/skip-step`, { step_id: stepId }, { headers: headers() });
    fetch();
  };

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Einrichtungsassistent</h2>
        <span className="text-sm font-medium text-[var(--brand-primary)]">Deine Business Studio Suite ist zu {data.progress}% eingerichtet</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-[var(--brand-primary)] h-2 rounded-full transition-all" style={{ width: `${data.progress}%` }} /></div>
      <div className="space-y-2">
        {data.steps.map((step) => (
          <Card key={step.id} className={`p-3 flex items-center gap-3 ${step.status === "completed" ? "bg-green-50 border-green-200" : step.status === "skipped" ? "opacity-50" : ""}`}>
            {step.status === "completed" ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" /> : <Circle className="w-5 h-5 text-gray-300 shrink-0" />}
            <div className="flex-1">
              <div className="font-medium text-sm">{step.title}</div>
              <div className="text-xs text-gray-500">{step.description}</div>
            </div>
            {step.status === "pending" && (
              <div className="flex gap-1">
                <Button size="sm" onClick={() => completeStep(step.id)}>Erledigt</Button>
                <Button size="sm" variant="ghost" onClick={() => skipStep(step.id)}><SkipForward className="w-3 h-3" /></Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

export function ApprovalView() {
  const [subTab, setSubTab] = useState("anfragen");
  const [rules, setRules] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const headers = useCallback(() => { const t = localStorage.getItem("token") || sessionStorage.getItem("token"); return { Authorization: `Bearer ${t}` }; }, []);

  const fetchRules = useCallback(async () => { try { const r = await axios.get(`${API}/api/approvals/rules`, { headers: headers() }); setRules(r.data || []); } catch (e) {} }, [headers]);
  const fetchRequests = useCallback(async () => { try { const r = await axios.get(`${API}/api/approvals/requests`, { headers: headers() }); setRequests(r.data || []); } catch (e) {} }, [headers]);

  useEffect(() => { fetchRules(); fetchRequests(); }, [fetchRules, fetchRequests]);

  const RuleForm = () => {
    const [form, setForm] = useState({ name: "", typ: "", bedingung: "betrag_groesser", grenzwert: 0, genehmiger: "" });
    const save = async () => {
      await axios.post(`${API}/api/approvals/rules`, form, { headers: headers() });
      toast.success("Regel erstellt"); setShowForm(false); fetchRules();
    };
    return (
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Neue Freigaberegel</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Name der Regel" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          <Select value={form.typ} onValueChange={v => setForm({...form, typ: v})}>
            <SelectTrigger><SelectValue placeholder="Typ..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="eingangsrechnung">Eingangsrechnung</SelectItem>
              <SelectItem value="ausgabe">Ausgabe</SelectItem>
              <SelectItem value="angebot">Angebot</SelectItem>
              <SelectItem value="urlaub">Urlaub</SelectItem>
              <SelectItem value="bestellung">Bestellung</SelectItem>
              <SelectItem value="dokument">Dokument</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" placeholder="Grenzwert (EUR)" value={form.grenzwert} onChange={e => setForm({...form, grenzwert: parseFloat(e.target.value) || 0})} />
          <Input placeholder="Genehmiger (E-Mail)" value={form.genehmiger} onChange={e => setForm({...form, genehmiger: e.target.value})} />
        </div>
        <div className="flex gap-2"><Button onClick={save}>Speichern</Button><Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button></div>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b pb-2">
        <button onClick={() => setSubTab("anfragen")} className={`px-3 py-1 rounded-md text-sm ${subTab === "anfragen" ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100"}`}>Offene Anfragen ({requests.length})</button>
        <button onClick={() => setSubTab("regeln")} className={`px-3 py-1 rounded-md text-sm ${subTab === "regeln" ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100"}`}>Regeln ({rules.length})</button>
      </div>

      {subTab === "anfragen" && (
        <div className="space-y-2">
          {requests.length === 0 ? <p className="text-gray-400 text-sm">Keine offenen Freigabeanfragen</p> : requests.map(req => (
            <Card key={req.id} className="p-3 flex items-center justify-between">
              <div><div className="font-medium text-sm">{req.titel}</div><div className="text-xs text-gray-500">{req.typ} | {req.betrag} EUR | von: {req.angefragt_von}</div></div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-green-600" onClick={async () => { await axios.post(`${API}/api/approvals/requests/${req.id}/approve`, {}, { headers: headers() }); toast.success("Genehmigt"); fetchRequests(); }}>Genehmigen</Button>
                <Button size="sm" variant="outline" onClick={async () => { await axios.post(`${API}/api/approvals/requests/${req.id}/reject`, {}, { headers: headers() }); toast.success("Abgelehnt"); fetchRequests(); }}>Ablehnen</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {subTab === "regeln" && (
        <div className="space-y-3">
          <Button onClick={() => setShowForm(true)}><Plus className="w-4 h-4 mr-1" />Neue Regel</Button>
          {showForm && <RuleForm />}
          {rules.map(rule => (
            <Card key={rule.id} className="p-3 flex items-center justify-between">
              <div><div className="font-medium text-sm">{rule.name}</div><div className="text-xs text-gray-500">{rule.typ} | ab {rule.grenzwert} EUR | Genehmiger: {rule.genehmiger}</div></div>
              <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/approvals/rules/${rule.id}`, { headers: headers() }); fetchRules(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
